import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { getOrdersAction } from '@kfc-global/react-shared/redux/Actions/orderAction';
import { getOrderHistory } from '@kfc-global/react-shared/redux/Actions/OrderHistoryAction';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { getUpdatedOrderDetails } from 'common/utilities/orderDetailsUtils';
import {
  isOrderCancelledOrRefund,
  isOrderCompletedOnFulfillmentDateTime,
  isOrderProcessCompleted,
  isRefundSuccessOrder,
  isRefundOrder,
  isCheckedInClosedOrder,
  isDasherDroppedOff,
} from 'common/utilities/orderStatusUtils';
import { getTimeString } from 'common/utilities/dayJsUtils';
import { getPastMonthYear, isValidObject, isValidValue, isDeliveryDisposition } from 'common/utilities/utils';
import { updateOrderTrackingDetailsInLocalStorage } from 'common/utilities/orderProcessingSummaryUtils';

export const defaultOrderHistoryDetails = {
  props: null,
  dispatch: null,
  customerId: null,
  tenantId: null,
  customer: null,
  userStatus: null,
  isMobileView: false,
  favoriteItems: null,
  selectedOrderId: null,
  currencySymbol: null,
  currencyConversionFactor: null,
  addHope: null,
  carryBag: null,
  pastOrderDateformat: '',
  pastOrderTimeFormat: '',
  orderDetails: {},
  orderHistory: [],
  currentOrderHistory: [],
  completedOrderHistory: [],
  displayNumOfRecords: 10,
  currentPage: 1,
  totalNumOfPages: 1,
  displayAllRecords: false,
  showHaveQuestionSection: true,
  showViewReceiptModal: false,
  showFavoriteModal: false,
  showReorderModal: false,
  isDarkMode: false,
  orderHistoryLoading: true,
  userOrderStateDispatch: null,
  cartDataService: null,
  showTaxLines: false,
  showTaxExcluded: true,
};

export const getOrderHistoryAction = orderHistoryDetails => {
  const { dispatch, customerId, tenantId } = orderHistoryDetails;
  const postBody = getPastMonthYear(12);
  dispatch(
    getOrderHistory(
      {
        customerId,
        tenantId,
      },
      postBody,
    ),
  );
};

export const getOrdersStatusAction = orderHistoryDetails => {
  const { dispatch, tenantId, selectedOrderId } = orderHistoryDetails;
  if (isValidValue(selectedOrderId)) {
    dispatch(
      getOrdersAction({
        orderId: selectedOrderId,
        tenantId,
      }),
    );
  }
};

export const updateOrderHistoryDetailsFromTenantProfileReducer = params => {
  const {
    props,
    dispatch,
    tenantReducer,
    profileReducer,
    updateSetOrderHistoryDetails,
    openReorderModal,
    userOrderStateDispatch,
  } = params;
  const {
    authoredContent,
    tenantId,
    currencySymbol,
    currencyConversionFactor,
    addHope,
    carryBag,
    pastOrderDateformat = '',
    pastOrderTimeFormat = '',
    componentOptions,
  } = tenantReducer;

  updateSetOrderHistoryDetails({
    props,
    dispatch,
    authoredContent,
    tenantId,
    currencySymbol,
    currencyConversionFactor,
    addHope,
    carryBag,
    pastOrderDateformat,
    pastOrderTimeFormat,
    customerId: profileReducer?.customerId,
    customer: profileReducer?.basic,
    favoriteItems: profileReducer?.favouriteItems || [],
    openReorderModal,
    userOrderStateDispatch,
    showTaxLines: componentOptions?.web?.cartPage?.showTaxLines,
    showTaxExcluded: componentOptions?.web?.checkout?.showTaxExcluded,
    enableGlobalOrderTracking: tenantReducer?.componentOptions?.web?.orderPages?.enableGlobalOrderTracking,
    enableRetryCountForPickupOrderTracking:
      tenantReducer?.componentOptions?.web?.orderPages?.enableRetryCountForPickupOrderTracking,
    pickupTrackOrderButtonLabel: tenantReducer?.componentOptions?.web?.orderPages?.pickupTrackOrderButtonLabel,
    enableScheduleOrder: tenantReducer?.componentOptions?.web?.dispositionConfig?.enableScheduleOrder,
    enableClickCollectScheduleOrder: tenantReducer?.componentOptions?.web?.dispositionConfig?.enableClickCollectScheduleOrder,
    allowScheduleOrderForDispositions: tenantReducer?.componentOptions?.web?.scheduledDelivery?.allowScheduleOrderForDispositions,
  });
};

export const updateOrderHistoryDetailsFromCartReducer = params => {
  const { cartReducer, updateSetOrderHistoryDetails } = params;
  const { cartData } = cartReducer;

  updateSetOrderHistoryDetails({
    cartDataService: cartData?.service,
  });
};

/*********************************************************************
 * updateOrderHistoryDetailsFromAppStateReducer
 * params
 * console.debug('updateOrderHistoryDetailsFromAppStateReducer => ');
 *********************************************************************/
export const updateOrderHistoryDetailsFromAppStateReducer = params => {
  const { appStateReducer, updateSetOrderHistoryDetails } = params;

  updateSetOrderHistoryDetails({
    userStatus: appStateReducer?.userStatus,
  });
};

/*********************************************************************
 * updateOrderHistoryDetailsFromPaymentsContentReducer
 * params
 * console.debug(updateOrderHistoryDetailsFromPaymentsContentReducer => ');
 *********************************************************************/
export const updateOrderHistoryDetailsFromPaymentsContentReducer = params => {
  const { paymentsContentReducer, updateSetOrderHistoryDetails } = params;

  updateSetOrderHistoryDetails({
    paymentsContentReducer: paymentsContentReducer,
  });
};

export const updateFavoriteOrderDetails = params => {
  const { profileReducer, orderDetails } = params;
  const { favouriteOrders } = profileReducer;
  if (isValidObject(orderDetails) && favouriteOrders?.length > 0) {
    const favOrder = favouriteOrders?.find(details => details?.id === orderDetails?.orderId);

    if (isValidObject(favOrder)) {
      orderDetails.isOrderSavedAsFavorite = true;
      orderDetails.favoriteOrderName = favOrder?.name;
    }
  }
  return orderDetails;
};

export const updateOrderHistoryDetailsFromOrderHistoryReducer = params => {
  const { orderHistoryReducer, orderHistoryDetails, updateSetOrderHistoryDetails } = params;

  updateSetOrderHistoryDetails({
    selectedOrderId: '',
    orderHistory:
      orderHistoryReducer?.orderHistory &&
      Array.isArray(orderHistoryReducer?.orderHistory) &&
      orderHistoryReducer?.orderHistory?.length > 0
        ? orderHistoryReducer?.orderHistory
            ?.sort((itemA, itemB) => itemB?.orderDateTimeInEpoch - itemA?.orderDateTimeInEpoch)
            ?.map(orderDetailsFromReducer => {
              const orderDetails = getUpdatedOrderDetails({
                postOrPastOrderDetails: { ...orderDetailsFromReducer, customer: orderHistoryDetails?.customer },
                orderDetails: orderHistoryDetails,
                paymentsContentReducer: orderHistoryDetails?.paymentsContentReducer,
              });
              orderDetails.enableGlobalOrderTracking = orderHistoryDetails?.enableGlobalOrderTracking;
              orderDetails.enableRetryCountForPickupOrderTracking =
                orderHistoryDetails?.enableRetryCountForPickupOrderTracking;
              return updateFavoriteOrderDetails({ orderDetails, ...params });
            })
        : [],
  });
};

export const updateOrderHistoryDetailsFromOrderHistory = params => {
  const { orderHistoryDetails, updateSetOrderHistoryDetails } = params;
  const { orderHistory } = orderHistoryDetails;
  updateSetOrderHistoryDetails({
    currentOrderHistory: orderHistory?.filter(
      details => !isOrderProcessCompleted(details) && !isOrderCompletedOnFulfillmentDateTime(details),
    ),
    completedOrderHistory: orderHistory?.filter(
      details => isOrderProcessCompleted(details) || isOrderCompletedOnFulfillmentDateTime(details),
    ),
  });
};

export const updateOrderHistoryDetailsFromOrderReducer = params => {
  const { orderReducer, orderHistoryDetails, updateSetOrderHistoryDetails } = params;
  updateSetOrderHistoryDetails({
    orderDetails: getUpdatedOrderDetails({
      postOrPastOrderDetails: orderReducer?.response,
      orderDetails: orderHistoryDetails,
      paymentsContentReducer: orderHistoryDetails?.paymentsContentReducer,
    }),
  });
};

export const onTrackOrderBtnClickHandler = params => {
  const { orderDetails, orderHistoryDetails, redirectTo } = params;
  const { props } = orderHistoryDetails;

  orderDetails?.orderId &&
    updateOrderTrackingDetailsInLocalStorage({
      trackDetails: orderHistoryDetails,
      orderDetails,
      redirectTo,
      props,
    });
};

export const getOrderingDateTime = params => {
  const { orderHistoryDetails, orderDetails } = params;
  const { orderDateOn, displayTodayDate, displayFormatOrderTime, fulfillmentDateTime, service } = orderDetails;
  const orderDateOnArray = orderDateOn?.split(" ");
  const orderCompleted = isDeliveryDisposition(service)
    ? isDasherDroppedOff(orderDetails)
    : isCheckedInClosedOrder(orderDetails);
  const { isMobileView } = orderHistoryDetails;
  if (isOrderCancelledOrRefund(orderDetails)) {
    return `${translateWithI18Next(
      isRefundSuccessOrder(orderDetails) || isRefundOrder(orderDetails)
        ? TRANSLATE_MAPPING_KEY?.REFUNDED
        : TRANSLATE_MAPPING_KEY?.CANCELED,
    )}: ${translateWithI18Next(orderDateOnArray[0]) + " " + orderDateOnArray[1] + " " + orderDateOnArray[2]}`;
  } else if (!orderCompleted && getTimeString(fulfillmentDateTime) && orderDateOn === displayTodayDate) {
    let scheduleText = `${translateWithI18Next(orderDateOnArray[0]) + " " + orderDateOnArray[1] + " " + orderDateOnArray[2]} ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.AT)} `;
    if (orderDateOn === displayTodayDate) {
      scheduleText =
        translateWithI18Next(TRANSLATE_MAPPING_KEY?.SCHEDULE) +
        ` ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.FOR)} ` +
        translateWithI18Next(TRANSLATE_MAPPING_KEY?.TODAY_AT);
    }
    return `${scheduleText} ${displayFormatOrderTime}`;
  }

  return `${translateWithI18Next(orderDateOnArray[0]) + " " + orderDateOnArray[1] + " " + orderDateOnArray[2]} ${
    isMobileView
      ? ' ' + displayFormatOrderTime
      : translateWithI18Next(TRANSLATE_MAPPING_KEY?.AT) + ' ' + displayFormatOrderTime
  }`;
};
